import {
  Button,
  ButtonGroup,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  SelectionList,
  Table,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import {
  ConversationLanguageCode,
  conversationLanguages,
} from 'lib-shared-languages';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMount } from 'react-use';

import type { SummaryMedia } from 'apis/userStorage';
import { HookFormSelectionListSingle } from 'components/hookForm/hookFormSelectionListSingle';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useServerStorage } from 'utils/useServerStorage';

import styles from './preferencesSummariesForm.module.scss';

const summaryMedia: Array<{ id: SummaryMedia; name: string }> = [
  { id: 'email', name: 'Email' },
  { id: 'interactivechat', name: 'Chat' },
  { id: 'messaging', name: 'Messaging' },
];

type FormValues = {
  summaryLanguage: ConversationLanguageCode | 'detect';
  summaryVisibilityState: {
    [media: string]: 'auto' | 'on-demand';
  };
};

const useConversationsLanguage = 'detect';
const summaryLanguageDefault = 'en';
type SummaryVisibilityState = {
  [mediaId in SummaryMedia]: 'auto' | 'on-demand';
};
const summaryVisibilityStateDefault: SummaryVisibilityState = {
  email: 'on-demand',
  interactivechat: 'on-demand',
  messaging: 'on-demand',
};

export const PreferencesSummariesForm = (): JSX.Element => {
  const { data, set } = useServerStorage('user');
  const form = useForm<FormValues>({
    mode: 'all',
    defaultValues: async () => {
      const aiOptions = data['agent_copilot'];
      const getSummaryLanguage = (): ConversationLanguageCode => {
        /**
         * Set language to English if it was previously set as "use conversation
         * language" in react hook form.
         */
        if (aiOptions?.summaryLanguage === useConversationsLanguage) {
          return summaryLanguageDefault;
        }
        return aiOptions?.summaryLanguage ?? summaryLanguageDefault;
      };
      return {
        summaryLanguage: getSummaryLanguage(),
        summaryVisibilityState: {
          ...summaryVisibilityStateDefault,
          ...aiOptions?.summaryVisibilityState,
        },
      };
    },
  });
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const {
    formState: { isDirty, dirtyFields, isSubmitting, isValid },
    reset,
  } = form;

  const onSubmit = useCallback(
    async (data) => {
      set({
        agent_copilot: (draft) => {
          Object.keys(dirtyFields).forEach((key) => {
            draft[key] = data[key];
          });
        },
      });
      reset(data);
      ampli.settingsSummarizationSaveChangesClicked();
    },
    [set, reset, dirtyFields],
  );

  const [languageSearchTerm, setLanguageSearchTerm] = useState('');

  const filteredConversationLanguages = useMemo(() => {
    const searchTerm = languageSearchTerm.trim();
    if (!searchTerm) {
      return conversationLanguages;
    }

    const fuse = new Fuse(conversationLanguages, {
      keys: ['name', 'code'],
      threshold: 0.2,
      distance: 200,
    });
    return fuse.search(searchTerm).map(({ item }) => item);
  }, [languageSearchTerm]);

  const handleLanguageSearchChange = useCallback((event) => {
    setLanguageSearchTerm(event.target.value);
  }, []);

  const selectedLanguage = form.watch('summaryLanguage');
  useEffect(() => {
    // Reset language search term when language is changed
    setLanguageSearchTerm('');
  }, [selectedLanguage]);

  useMount(() => {
    /**
     * Set language to English if it was previously set as "use conversation
     * language" in the server storage.
     */
    const aiOptions = data['agent_copilot'];
    if (aiOptions?.summaryLanguage === useConversationsLanguage) {
      set({
        agent_copilot: (draft) => {
          draft.summaryLanguage = summaryLanguageDefault;
        },
      });
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Page.Section
          title="Summarization"
          controls={
            <>
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={!isDirty || !isValid || isSubmitting}
              >
                Save changes
              </Button>
            </>
          }
        >
          <FormGrid>
            <FormField span={isSmallScreen ? 6 : 2}>
              <HookFormSelectionListSingle
                name="summaryLanguage"
                label="Summary language"
                helpIcon={{
                  dark: true,
                  text: 'Set the language that you want to use for summaries.',
                }}
                componentProps={{
                  getItemTitleByValue: (id) => {
                    return (
                      conversationLanguages.find((lang) => lang.code === id)
                        ?.name || ''
                    );
                  },
                  filter: {
                    value: languageSearchTerm,
                    onChange: handleLanguageSearchChange,
                  },
                  placeholder: 'Search...',
                  selectionListProps: {
                    className: styles.selectionList,
                  },
                }}
              >
                {filteredConversationLanguages.map((lang) => (
                  <SelectionList.Item key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectionList.Item>
                ))}
              </HookFormSelectionListSingle>
            </FormField>

            <FormField span={6}>
              <FieldWrapper
                label="When to show summarization"
                helpIcon={{
                  dark: true,
                  text: 'Choose when you would like summaries to be shown inside conversations.',
                }}
              >
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Channel</Table.Th>
                      <Table.Th width="200px">Show</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {summaryMedia.map((media) => {
                      const visibilityState =
                        form.watch(`summaryVisibilityState.${media.id}`) ??
                        'auto';
                      return (
                        <Table.Tr key={media.id}>
                          <Table.Td>{media.name}</Table.Td>
                          <Table.Td>
                            <FlexBox>
                              <ButtonGroup>
                                <Button
                                  variant={
                                    visibilityState === 'auto'
                                      ? 'primary'
                                      : 'secondary'
                                  }
                                  onClick={() => {
                                    form.setValue(
                                      `summaryVisibilityState.${media.id}`,
                                      'auto',
                                      { shouldDirty: true },
                                    );

                                    ampli.settingsSummarizationShowAutomaticallyClicked(
                                      { channel: media.name },
                                    );
                                  }}
                                >
                                  Automatically
                                </Button>
                                <Button
                                  variant={
                                    visibilityState === 'on-demand'
                                      ? 'primary'
                                      : 'secondary'
                                  }
                                  onClick={() => {
                                    form.setValue(
                                      `summaryVisibilityState.${media.id}`,
                                      'on-demand',
                                      { shouldDirty: true },
                                    );

                                    ampli.settingsSummarizationShowOnDemandClicked(
                                      { channel: media.name },
                                    );
                                  }}
                                >
                                  On demand
                                </Button>
                              </ButtonGroup>
                            </FlexBox>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </FieldWrapper>
            </FormField>
          </FormGrid>
        </Page.Section>
      </form>
    </FormProvider>
  );
};
